import {
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  LoadingContainer,
  PageContainer,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput,
  StyledTooltip
} from 'app/components/common/common-styles';
import { BodyText, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { StyledCaption } from 'app/components/management/apps/create/apps-create-step-1';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import { RewardsType } from 'app/console-api/apps/types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { appsCreateStep2ValidationSchema } from 'app/components/management/apps/common/apps-validation-schema';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { AppCategoriesTypeControl } from 'app/components/management/apps/settings/categories/app-categories-type-control';
import { AppCategory, getAppCategories } from 'app/console-api/apps/categories';
import RewardsTypesControl from 'app/components/management/apps/settings/rewards/app-rewards-type-control';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import { updateIntegrationData } from 'app/console-api';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { APPS_PATH, APPS_VIEW_PATH } from 'app/route/paths';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { WizardProgress } from 'app/components/management/apps/create/wizard-progress';
import { InfoSection } from 'app/components/management/common/info-section/info-section';
import { AppIntegrationDetails } from 'app/components/management/apps/common/app-integration-details';
import { InfoAccordion } from 'app/components/management/common/info-accordion/info-accordion';
import InfoIcon from 'assets/icons/ic-info.svg';

export const AppsEditIntegration = () => {
  const { app, setApp } = useTabsCtx();
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [categories, setCategories] = useState<AppCategory[]>([]);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useTabsCtx();
  const [searchParams] = useSearchParams();
  const referer = searchParams.get('referer');

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: 'Edit Integration'
      }
    ]);
    return () => setBreadcrumbs([]);
  }, []);

  const [formData, setFormData] = useState({
    url: app?.url || '',
    claimNotificationUrl: app?.claimNotificationUrl || ''
  });
  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(appsCreateStep2ValidationSchema),
    defaultValues: formData
  });

  useEffect(() => {
    if (!app) return;
    getRewardsTypes(app.id)
      .then((response) => setRewardsTypes(response.data || []))
      .catch((error: any) => {
        console.error('Failed to fetch rewards types', error);
      });
    getAppCategories(app.id).then(
      (response) => setCategories(response.data || []),
      (error: any) => {
        console.error('Failed to fetch categories', error);
      }
    );
  }, [app]);

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate(
      referer === 'onboarding'
        ? generatePath(APPS_PATH, { appID: app!.id })
        : generatePath(APPS_VIEW_PATH, { appID: app!.id })
    );
  };

  const handleSubmitForm = async () => {
    updateIntegrationData(app!.id, formData)
      .then((res) => {
        setApp(res);
        setOpenSuccessModal(true);
      })
      .catch((error) => {
        setUnexpectedError({
          code: error.statusCode.toString(),
          message: error.message
        });
      });
  };

  const sentData = () => {
    handleSubmit(handleSubmitForm)();
  };

  if (!app) {
    return (
      <LoadingContainer>
        <SkeletonLoader height={'50vh'} />
      </LoadingContainer>
    );
  }

  if (unexpectedError) {
    return (
      <PageContainer>
        <ErrorWindow
          withModal
          title={unexpectedError.code}
          information={unexpectedError.message}
          icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FlexRow justify={'center'} align={'center'}>
        <FormContainer>
          <SuccessEntityWindow
            dismissLabel={'Close'}
            onDismiss={handleCloseSuccessModal}
            isOpen={openSuccessModal}
            icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
            title="App details saved"
            information="App details saved successfully"
          />
          <FlexColumn gap={40}>
            {referer === 'onboarding' && <WizardProgress step={2}></WizardProgress>}
            <FlexColumn gap={20}>
              <StyledCaption>
                <BodyText size={1} scale={'lg'}>
                  Integration data
                </BodyText>
              </StyledCaption>
              {referer === 'onboarding' && (
                <InfoSection
                  title={'Technical setup required'}
                  content={
                    'No stress — you can skip this step or pass it to someone who knows the code.'
                  }
                ></InfoSection>
              )}
              <FlexColumn gap={20}>
                <StyledInput
                  required
                  name={'url'}
                  placeholder={'https://t.me/my_name_bot/my_app'}
                  label={
                    <FlexRow align={'center'} gap={4}>
                      <BodyText size={1}>App URL</BodyText>
                      <StyledTooltip
                        tooltipContent={'This is the Direct Link to your Telegram app'}
                      >
                        <SvgIcon src={InfoIcon} color={'gray'} />
                      </StyledTooltip>
                    </FlexRow>
                  }
                  value={formData.url}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleInputChange('url', e.target.value)
                  }
                />
                {errors?.url?.message && (
                  <ErrorTextContainer justify={'flex-start'}>
                    <ErrorText size={3}>{errors?.url?.message}</ErrorText>
                  </ErrorTextContainer>
                )}
                <FlexColumn gap={24}>
                  <StyledInput
                    required
                    name={'claimNotificationUrl'}
                    label={
                      <FlexRow align={'center'} gap={4}>
                        <BodyText size={1}>Claim Notification URL</BodyText>
                        <StyledTooltip
                          tooltipContent={
                            'Backend API endpoint to receive successful claim notifications and reward users'
                          }
                        >
                          <SvgIcon src={InfoIcon} color={'gray'} />
                        </StyledTooltip>
                      </FlexRow>
                    }
                    placeholder={'https://your-api.com/claim-succeeded'}
                    value={formData.claimNotificationUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('claimNotificationUrl', e.target.value)
                    }
                  />
                  {errors?.claimNotificationUrl?.message && (
                    <ErrorTextContainer justify={'flex-start'}>
                      <ErrorText size={3}>{errors?.claimNotificationUrl?.message}</ErrorText>
                    </ErrorTextContainer>
                  )}
                </FlexColumn>
              </FlexColumn>
              <RewardsTypesControl initialRewards={rewardsTypes} appID={app?.id || ''} />
              <AppCategoriesTypeControl categories={categories} appID={app?.id || ''} />
            </FlexColumn>
            <InfoAccordion
              {...app}
              style={{}}
              disableClickAway={true}
              renderContent={() => <AppIntegrationDetails app={app} />}
            >
              Integration instructions
            </InfoAccordion>
            <FlexRow gap={20}>
              <StyledDismissButton color={'secondaryBlue'} onClick={handleCloseSuccessModal}>
                {referer === 'onboarding' ? 'Skip' : 'Cancel'}
              </StyledDismissButton>
              <StyledConfirmButton color="primaryBlue" onClick={sentData}>
                Save
              </StyledConfirmButton>
            </FlexRow>
          </FlexColumn>
        </FormContainer>
      </FlexRow>
    </PageContainer>
  );
};
